/**
 * Time Tracking Module
 * Handles service date picker and start/end time tracking
 */

import { getTodayLocal } from '../utils/date-utils.js';

// Global state
window.serviceDate = null;
window.startTime = null;
window.endTime = null;
window.currentServiceLogId = null; // Track service log ID for updates

/**
 * Initialize date picker with today's date and check for in-progress services
 */
export async function initDatePicker() {
    window.serviceDate = getTodayLocal();

    // Update UI display
    updateDateDisplay();

    console.log('📅 Date picker initialized with today:', window.serviceDate);

    // Check for in-progress services and restore state
    await checkInProgressServices();
}

/**
 * Check for in-progress services and display banner
 */
async function checkInProgressServices() {
    try {
        const { data, error } = await window.supabaseClient
            .from('service_logs')
            .select(`
                id,
                service_started_at,
                service_type,
                boat_id,
                customer_id,
                boats(id, name)
            `)
            .eq('in_progress', true)
            .order('service_started_at', { ascending: false });

        if (error) {
            console.error('Error checking in-progress services:', error);
            return;
        }

        if (data && data.length > 0) {
            console.log('🟢 Found in-progress services:', data);
            displayInProgressBanner(data);
        }
    } catch (error) {
        console.error('Error checking in-progress services:', error);
    }
}

/**
 * Display banner for in-progress services
 */
function displayInProgressBanner(services) {
    // Remove existing banner if any
    const existingBanner = document.getElementById('inProgressBanner');
    if (existingBanner) {
        existingBanner.remove();
    }

    // Create banner HTML
    const servicesHTML = services.map(service => {
        const startTime = new Date(service.service_started_at);
        const now = new Date();
        const diffMinutes = Math.floor((now - startTime) / 1000 / 60);
        const boatName = service.boats?.name || 'Unknown';

        return `
            <div class="in-progress-service" data-service-id="${service.id}" style="cursor: pointer;">
                <strong>${boatName}</strong> (${diffMinutes}m ago)
                <button onclick="window.loadInProgressService('${service.id}')" style="margin-left: 10px; padding: 4px 8px; font-size: 12px;">
                    Resume
                </button>
            </div>
        `;
    }).join('');

    const bannerHTML = `
        <div id="inProgressBanner" style="
            position: fixed;
            top: 60px;
            left: 0;
            right: 0;
            background: #4CAF50;
            color: white;
            padding: 12px 20px;
            z-index: 1000;
            display: flex;
            align-items: center;
            gap: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        ">
            <div style="font-weight: bold;">🟢 Open Services:</div>
            <div style="display: flex; gap: 15px; flex-wrap: wrap; align-items: center;">
                ${servicesHTML}
            </div>
        </div>
    `;

    // Insert banner at top of page
    document.body.insertAdjacentHTML('afterbegin', bannerHTML);
}

/**
 * Load and resume an in-progress service
 */
window.loadInProgressService = async function(serviceLogId) {
    try {
        console.log('📂 Loading in-progress service:', serviceLogId);

        const { data, error } = await window.supabaseClient
            .from('service_logs')
            .select(`
                *,
                boats(*)
            `)
            .eq('id', serviceLogId)
            .single();

        if (error || !data) {
            console.error('Error loading service:', error);
            alert('Failed to load service');
            return;
        }

        // Parse and restore timestamps first
        const startDateTime = new Date(data.service_started_at);
        window.serviceDate = data.service_started_at.split('T')[0];
        window.startTime = startDateTime.toTimeString().split(' ')[0];

        // Fetch customer data with boat included
        // Note: customer_id in service_logs can be:
        // - A Stripe customer ID (e.g., cus_XXX)
        // - A placeholder ID for customers without Stripe (e.g., supabase_UUID)
        // - A direct UUID (legacy)
        let customerData = null;
        if (data.customer_id) {
            let result;

            // If it's a Stripe customer ID, query by stripe_customer_id column
            if (data.customer_id.startsWith('cus_')) {
                result = await window.supabaseClient
                    .from('customers')
                    .select('*')
                    .eq('stripe_customer_id', data.customer_id)
                    .maybeSingle();
            } else {
                // For placeholder IDs or direct UUIDs
                let lookupId = data.customer_id;
                if (data.customer_id.startsWith('supabase_')) {
                    lookupId = data.customer_id.replace('supabase_', '');
                }

                // Query by id column (UUID)
                result = await window.supabaseClient
                    .from('customers')
                    .select('*')
                    .eq('id', lookupId)
                    .maybeSingle();
            }

            if (result.data) {
                customerData = result.data;
                // Flatten boat data into customer object (like universal search does)
                if (data.boats) {
                    customerData.boat_id = data.boats.id || data.boat_id;
                    customerData.boat_name = data.boats.name;
                    customerData.boat_length = data.boats.length;
                    customerData.boat_make = data.boats.make;
                    customerData.boat_model = data.boats.model;
                    customerData.boat_type = data.boats.type;
                    customerData.hull_type = data.boats.hull_type;
                    customerData.propeller_count = data.boats.propeller_count;
                    customerData.marina = data.boats.marina;
                    customerData.dock = data.boats.dock;
                    customerData.slip = data.boats.slip;
                    // Also keep the nested boat object for compatibility
                    customerData.boat = data.boats;
                }
            }
        }

        if (!customerData) {
            const boatName = data.boats?.name || 'Unknown boat';
            console.error('Could not load customer data for customer_id:', data.customer_id);
            console.error('This might be a Stripe customer ID not linked to database');

            const shouldDelete = confirm(
                `Cannot resume service for "${boatName}" - customer data not found.\n\n` +
                `Customer ID: ${data.customer_id}\n\n` +
                `This may be orphaned test data. Would you like to delete this in-progress service?`
            );

            if (shouldDelete) {
                try {
                    await window.supabaseClient
                        .from('service_logs')
                        .delete()
                        .eq('id', serviceLogId);
                    console.log('✅ Deleted orphaned service log:', serviceLogId);
                    // Refresh the banner
                    await checkInProgressServices();
                    // Remove the banner if no more in-progress services
                    const banner = document.getElementById('inProgressBanner');
                    if (banner) {
                        const remainingServices = banner.querySelectorAll('.in-progress-service');
                        if (remainingServices.length <= 1) {
                            banner.remove();
                        }
                    }
                } catch (deleteError) {
                    console.error('Failed to delete service log:', deleteError);
                    alert('Failed to delete the orphaned service');
                }
            }
            return;
        }

        // Restore global state BEFORE triggering customer selection
        window.currentServiceLogId = data.id;
        window.currentServiceKey = data.service_type;

        // Trigger customer/boat selection to populate the entire form
        // This will set selectedCustomer, selectedBoat, and render the service form
        if (window.selectUniversalSearchCustomer) {
            await window.selectUniversalSearchCustomer(customerData);
        }

        // After form is loaded, update UI to show service is already started
        setTimeout(() => {
            const startBtn = document.getElementById('startServiceBtn');
            const startTimeDisplay = document.getElementById('startTimeDisplay');
            const startTimeText = document.getElementById('startTimeText');
            const endServiceContainer = document.getElementById('endServiceContainer');
            const endBtn = document.getElementById('endServiceBtn');

            const dateObj = new Date(window.serviceDate + 'T00:00:00');
            const dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

            if (startBtn) {
                startBtn.disabled = true;
                startBtn.textContent = `✅ Started`;
            }

            if (startTimeDisplay && startTimeText) {
                startTimeText.textContent = `Started: ${dateStr} at ${window.startTime}`;
                startTimeDisplay.style.display = 'block';
            }

            if (endServiceContainer) {
                endServiceContainer.style.display = 'block';
            }

            if (endBtn) {
                endBtn.disabled = false;
            }

            console.log('✅ Service resumed:', data.boats.name, 'at', window.startTime);
        }, 500); // Wait for form to render

    } catch (error) {
        console.error('Error loading service:', error);
        alert('Error loading service');
    }
};

/**
 * Update date display in UI
 */
function updateDateDisplay() {
    const dateDisplay = document.getElementById('serviceDateDisplay');
    if (!dateDisplay) return;

    if (window.serviceDate) {
        const date = new Date(window.serviceDate + 'T00:00:00');
        const formatted = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        dateDisplay.textContent = formatted;
    } else {
        dateDisplay.textContent = 'Not set';
    }
}

/**
 * Open date picker modal
 */
export function editServiceDate() {
    const newDate = prompt('Enter service date (YYYY-MM-DD):', window.serviceDate || '');
    if (newDate && /^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
        window.serviceDate = newDate;
        updateDateDisplay();
        console.log('📅 Service date updated:', window.serviceDate);
    } else if (newDate) {
        alert('Invalid date format. Please use YYYY-MM-DD');
    }
}

/**
 * Start service - capture start date and time and save to database
 */
export async function startService() {
    const now = new Date();

    // Always capture today's date when starting service
    window.serviceDate = getTodayLocal();
    updateDateDisplay();

    // Capture start time in 24-hour format
    window.startTime = now.toTimeString().split(' ')[0]; // HH:MM:SS

    // Construct full timestamp for database
    const serviceStartedAt = `${window.serviceDate}T${window.startTime}`;

    // Update UI - show loading state
    const startBtn = document.getElementById('startServiceBtn');
    const startTimeDisplay = document.getElementById('startTimeDisplay');
    const startTimeText = document.getElementById('startTimeText');

    if (startBtn) {
        startBtn.disabled = true;
        startBtn.textContent = `⏳ Starting...`;
    }

    try {
        // Save to database - create service_log with start timestamp
        const boat = window.selectedBoat;
        const customer = window.selectedCustomer || window.adminApp?.selectedCustomer;

        if (!boat || !customer) {
            alert('Please select a customer and boat first');
            if (startBtn) {
                startBtn.disabled = false;
                startBtn.textContent = `▶️ Start Service`;
            }
            return;
        }

        // Get service type from current selection
        const serviceType = window.currentServiceKey;
        if (!serviceType) {
            alert('Please select a service type first');
            if (startBtn) {
                startBtn.disabled = false;
                startBtn.textContent = `▶️ Start Service`;
            }
            return;
        }

        console.log('💾 Creating service log with start timestamp:', serviceStartedAt, 'Service type:', serviceType);

        const { data, error } = await window.supabaseClient
            .from('service_logs')
            .insert({
                boat_id: boat.id,
                customer_id: customer.id,
                service_type: serviceType,
                service_started_at: serviceStartedAt,
                in_progress: true,
                service_date: window.serviceDate
            })
            .select()
            .single();

        if (error) {
            console.error('❌ Failed to save service start time:', error);
            alert(`Failed to start service: ${error.message}`);
            if (startBtn) {
                startBtn.disabled = false;
                startBtn.textContent = `▶️ Start Service`;
            }
            return;
        }

        // Store service log ID for later updates
        window.currentServiceLogId = data.id;
        console.log('✅ Service log created with ID:', data.id);

        // Format date for display
        const dateObj = new Date(window.serviceDate + 'T00:00:00');
        const dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        // Update UI - success state
        if (startBtn) {
            startBtn.textContent = `✅ Started`;
        }

        if (startTimeDisplay && startTimeText) {
            startTimeText.textContent = `Started: ${dateStr} at ${window.startTime}`;
            startTimeDisplay.style.display = 'block';
        }

        // Show and enable end service button
        const endServiceContainer = document.getElementById('endServiceContainer');
        const endBtn = document.getElementById('endServiceBtn');

        if (endServiceContainer) {
            endServiceContainer.style.display = 'block';
        }

        if (endBtn) {
            endBtn.disabled = false;
        }

        // Scroll to service conditions section
        setTimeout(() => {
            const conditionsSection = document.getElementById('conditionsSection');
            if (conditionsSection) {
                conditionsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300);

        console.log('⏱️ Service started and saved:', window.serviceDate, window.startTime);

        // Update banner to show new in-progress service
        await checkInProgressServices();

    } catch (error) {
        console.error('❌ Error starting service:', error);
        alert(`Error starting service: ${error.message}`);
        if (startBtn) {
            startBtn.disabled = false;
            startBtn.textContent = `▶️ Start Service`;
        }
    }
}

/**
 * End service - capture end time and save to database
 */
export async function endService() {
    if (!window.startTime) {
        alert('Please start the service first');
        return;
    }

    if (!window.currentServiceLogId) {
        alert('No active service log found. Please start the service first.');
        return;
    }

    const now = new Date();
    window.endTime = now.toTimeString().split(' ')[0]; // HH:MM:SS

    // Construct full timestamp for database
    const serviceEndedAt = `${window.serviceDate}T${window.endTime}`;

    // Update UI - show loading state
    const endBtn = document.getElementById('endServiceBtn');
    const endTimeDisplay = document.getElementById('endTimeDisplay');
    const endTimeText = document.getElementById('endTimeText');
    const durationDisplay = document.getElementById('serviceDuration');

    if (endBtn) {
        endBtn.disabled = true;
        endBtn.textContent = `⏳ Ending...`;
    }

    try {
        // Calculate duration in hours (decimal)
        const [startH, startM, startS] = window.startTime.split(':').map(Number);
        const [endH, endM, endS] = window.endTime.split(':').map(Number);

        const startSeconds = startH * 3600 + startM * 60 + startS;
        const endSeconds = endH * 3600 + endM * 60 + endS;

        let diffSeconds = endSeconds - startSeconds;
        if (diffSeconds < 0) diffSeconds += 86400; // Handle overnight services

        const totalHours = (diffSeconds / 3600).toFixed(2); // Decimal hours for database

        console.log('💾 Updating service log with end timestamp:', serviceEndedAt, 'Duration:', totalHours, 'hours');

        // Update service_log in database
        const { error } = await window.supabaseClient
            .from('service_logs')
            .update({
                service_ended_at: serviceEndedAt,
                total_hours: parseFloat(totalHours),
                in_progress: false
            })
            .eq('id', window.currentServiceLogId);

        if (error) {
            console.error('❌ Failed to save service end time:', error);
            alert(`Failed to end service: ${error.message}`);
            if (endBtn) {
                endBtn.disabled = false;
                endBtn.textContent = `⏹️ End Service`;
            }
            return;
        }

        console.log('✅ Service log updated successfully');

        // Update UI - success state
        if (endBtn) {
            endBtn.textContent = `⏹️ Ended at ${window.endTime}`;
        }

        if (endTimeDisplay && endTimeText) {
            endTimeText.textContent = `Ended: ${window.endTime}`;
            endTimeDisplay.style.display = 'block';
        }

        // Calculate and display duration in human-readable format
        const duration = calculateDuration(window.startTime, window.endTime);
        if (durationDisplay && duration) {
            durationDisplay.textContent = `Duration: ${duration} (${totalHours} hrs)`;
        }

        console.log('⏹️ Service ended at:', window.endTime);
        console.log('⏱️ Duration:', duration, `(${totalHours} hrs)`);

        // Update banner to remove completed service
        await checkInProgressServices();

    } catch (error) {
        console.error('❌ Error ending service:', error);
        alert(`Error ending service: ${error.message}`);
        if (endBtn) {
            endBtn.disabled = false;
            endBtn.textContent = `⏹️ End Service`;
        }
    }
}

/**
 * Calculate duration between start and end time
 */
function calculateDuration(startTime, endTime) {
    if (!startTime || !endTime) return null;

    const [startH, startM, startS] = startTime.split(':').map(Number);
    const [endH, endM, endS] = endTime.split(':').map(Number);

    const startSeconds = startH * 3600 + startM * 60 + startS;
    const endSeconds = endH * 3600 + endM * 60 + endS;

    let diffSeconds = endSeconds - startSeconds;
    if (diffSeconds < 0) diffSeconds += 86400; // Handle overnight services

    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);

    return `${hours}h ${minutes}m`;
}

/**
 * Edit start date and time manually
 */
export function editStartTime() {
    // First edit the date
    const currentDate = window.serviceDate || getTodayLocal();
    const newDate = prompt('Enter service date (YYYY-MM-DD):', currentDate);

    if (!newDate) return; // User cancelled

    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
        alert('Invalid date format. Please use YYYY-MM-DD');
        return;
    }

    // Then edit the time
    const currentTime = window.startTime || '';
    const newTime = prompt('Enter start time (HH:MM or HH:MM:SS):', currentTime);

    if (!newTime) return; // User cancelled

    if (!/^\d{2}:\d{2}(:\d{2})?$/.test(newTime)) {
        alert('Invalid time format. Please use HH:MM or HH:MM:SS');
        return;
    }

    // Update both date and time
    window.serviceDate = newDate;
    window.startTime = newTime.length === 5 ? `${newTime}:00` : newTime;

    // Update date display
    updateDateDisplay();

    // Update start time display
    const dateObj = new Date(window.serviceDate + 'T00:00:00');
    const dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    const startTimeText = document.getElementById('startTimeText');
    if (startTimeText) {
        startTimeText.textContent = `Started: ${dateStr} at ${window.startTime}`;
    }

    // Recalculate duration if end time exists
    if (window.endTime) {
        const durationDisplay = document.getElementById('serviceDuration');
        if (durationDisplay) {
            const duration = calculateDuration(window.startTime, window.endTime);
            if (duration) {
                durationDisplay.textContent = `Duration: ${duration}`;
            }
        }
    }

    console.log('⏱️ Start date and time manually updated:', window.serviceDate, window.startTime);
}

/**
 * Edit end time manually
 */
export function editEndTime() {
    const currentTime = window.endTime || '';
    const newTime = prompt('Enter end time (HH:MM or HH:MM:SS):', currentTime);

    if (newTime && /^\d{2}:\d{2}(:\d{2})?$/.test(newTime)) {
        window.endTime = newTime.length === 5 ? `${newTime}:00` : newTime;

        // Update display
        const endTimeText = document.getElementById('endTimeText');
        const endBtn = document.getElementById('endServiceBtn');

        if (endTimeText) {
            endTimeText.textContent = `Ended: ${window.endTime}`;
        }

        if (endBtn) {
            endBtn.textContent = `⏹️ Ended at ${window.endTime}`;
        }

        // Recalculate duration
        if (window.startTime) {
            const durationDisplay = document.getElementById('serviceDuration');
            if (durationDisplay) {
                const duration = calculateDuration(window.startTime, window.endTime);
                if (duration) {
                    durationDisplay.textContent = `Duration: ${duration}`;
                }
            }
        }

        console.log('⏹️ End time manually updated:', window.endTime);
    } else if (newTime) {
        alert('Invalid time format. Please use HH:MM or HH:MM:SS');
    }
}

// Expose functions to window
window.editServiceDate = editServiceDate;
window.startService = startService;
window.endService = endService;
window.editStartTime = editStartTime;
window.editEndTime = editEndTime;
